import { COUNTRY_CODE_LOCALE } from '@advanza/constants'
import { Icon } from '@advanza/ui'
import { useSubscriptionArea } from 'hooks/providerHooks'
import React, { useMemo, useState } from 'react'
import style from './ServiceAreaListUtlimate.module.css'

const ServiceAreaListUltimate = ({ subscriptionId, isLeadEstimator }) => {
    const {
        municipalities,
        subscriptionMunicipalities,
        toggleMunicipality,
        saveMunicipalities,
        setCenter,
        municipalitiesGrouped,
        toggleMunicipalityMany,
        subscription,
    } = useSubscriptionArea(subscriptionId, isLeadEstimator)
    const [showMore, setShowMore] = useState({})
    const [searchTerm, setSearchTerm] = useState('')

    const isLoaded =
        subscription &&
        municipalitiesGrouped &&
        municipalitiesGrouped[subscription.country_code] &&
        municipalities &&
        subscriptionMunicipalities

    const sortedIdsByRegion = useMemo(() => {
        const countryData = municipalitiesGrouped?.[subscription?.country_code]
        if (!countryData || !municipalities) return {}
        const collator = new Intl.Collator(COUNTRY_CODE_LOCALE[subscription.country_code] ?? 'en', {
            sensitivity: 'base',
        })
        const sorted = {}
        for (const [regionName, ids] of Object.entries(countryData)) {
            sorted[regionName] = [...ids].sort((a, b) =>
                collator.compare(municipalities[a].name, municipalities[b].name),
            )
        }
        return sorted
    }, [municipalitiesGrouped, subscription?.country_code, municipalities])

    // Filter cities based on search term
    const filteredMunicipalitiesGrouped = useMemo(() => {
        if (
            !isLoaded ||
            !municipalitiesGrouped ||
            !subscription?.country_code ||
            !municipalitiesGrouped[subscription.country_code] ||
            !searchTerm.trim()
        ) {
            return municipalitiesGrouped?.[subscription?.country_code] || {}
        }

        const filtered = {}
        const searchLower = searchTerm.toLowerCase()

        Object.keys(municipalitiesGrouped[subscription.country_code]).forEach((regionName) => {
            const municipalityIds = municipalitiesGrouped[subscription.country_code][regionName]

            // Check if any city in this region matches search term
            const matchingIds = municipalityIds.filter((id) => {
                const municipality = municipalities[id]
                return municipality && municipality.name.toLowerCase().includes(searchLower)
            })

            // Include region only if it has matching cities
            if (matchingIds.length > 0) {
                filtered[regionName] = matchingIds
            }
        })

        return filtered
    }, [isLoaded, municipalitiesGrouped, subscription, municipalities, searchTerm])

    // Auto-expand regions that have matching cities when searching
    const autoExpandedRegions = useMemo(() => {
        if (!searchTerm.trim()) {
            return {}
        }

        const expanded = {}
        Object.keys(filteredMunicipalitiesGrouped).forEach((regionName) => {
            expanded[regionName] = true
        })
        return expanded
    }, [filteredMunicipalitiesGrouped, searchTerm])

    return (
        <div className={style.root}>
            {isLoaded && (
                <>
                    <div className={style.searchContainer}>
                        <input
                            type="text"
                            placeholder="Search municipalities..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={style.searchInput}
                        />
                    </div>
                    {Object.keys(filteredMunicipalitiesGrouped).map((regionName) => {
                        const ids = filteredMunicipalitiesGrouped[regionName]
                        const nrSelected = ids.filter((id) =>
                            subscriptionMunicipalities.includes(id),
                        ).length
                        const nrTotal = ids.length
                        const hasAll = nrSelected === nrTotal
                        const expanded = searchTerm.trim()
                            ? autoExpandedRegions[regionName] || showMore[regionName]
                            : showMore[regionName]
                        return (
                            <div key={regionName} className={style.item}>
                                <div className={style.region}>
                                    <div
                                        onClick={() => {
                                            toggleMunicipalityMany(ids, hasAll)
                                            !hasAll &&
                                                setCenter({
                                                    ...municipalities[
                                                        ids[Math.round(ids.length / 2)]
                                                    ],
                                                    mapZoom: 8,
                                                })
                                            setTimeout(saveMunicipalities, 10)
                                        }}>
                                        <Icon
                                            name={hasAll ? 'check_box' : 'check_box_outline_blank'}
                                        />
                                    </div>
                                    <div
                                        className={style.regionName}
                                        onClick={() =>
                                            setShowMore({
                                                ...showMore,
                                                [regionName]: expanded !== true,
                                            })
                                        }>
                                        <div>
                                            {regionName}
                                            {nrSelected > 0 && (
                                                <div>
                                                    <b>{nrSelected}</b> van de {nrTotal}{' '}
                                                    geselecteerd
                                                </div>
                                            )}
                                        </div>
                                        <Icon name={expanded ? 'expand_less' : 'expand_more'} />
                                    </div>
                                </div>
                                {expanded && (
                                    <div className={style.expanded}>
                                        {(sortedIdsByRegion[regionName] ?? ids).map((id) => {
                                            const mun = municipalities[id]
                                            const isChecked =
                                                subscriptionMunicipalities.includes(id)
                                            return (
                                                <div key={id}>
                                                    <button
                                                        onClick={() => {
                                                            toggleMunicipality(id)
                                                            setCenter(municipalities[id])
                                                            saveMunicipalities()
                                                        }}>
                                                        <Icon
                                                            name={
                                                                isChecked
                                                                    ? 'check_box'
                                                                    : 'check_box_outline_blank'
                                                            }
                                                        />{' '}
                                                        {mun.name}
                                                    </button>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </>
            )}
        </div>
    )
}

export default ServiceAreaListUltimate
